import { useEffect, useState } from 'react';

import date from 'date-and-time';

import { createColumnHelper } from '@tanstack/react-table';
import {
  CloudArrowUpIcon,
  ExclamationCircleIcon,
  PencilIcon,
  TrashIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';

import { Card } from '../../components/base/Card';
import { DataTable } from '../../components/base/Table';
import { Button } from '../../components/base/Button';
import { applianceQueryKeys } from '../../data/QueryKeys';
import { useUserContext } from '../../contexts/UserContext';
import { IAppliance } from '../../types/Appliance/Appliance';
import { usePageTitle } from '../../contexts/PageTitleContext';
import { useClipboardCopy } from '../../hooks/useClipboardCopy';
import { ConfirmDelete } from '../../components/shared/ConfirmDelete';
import { useApplianceLookups } from '../../hooks/useApplianceLookups';
import { SectionLoading } from '../../components/shared/SectionLoading';
import useApplianceMutations from '../../data/appliance/useApplianceMutations';
import { CreateEditAppliance } from '../../components/Appliance/CreateEditAppliance';
import { ApplianceFirmwareUpdate } from '../../components/Appliance/ApplianceFirmwareUpdate';
import { useNavigate } from 'react-router';

const colHelper = createColumnHelper<IAppliance>();

export function AppliancesPage() {
  const navigate = useNavigate();
  const { isInstaller } = useUserContext();
  const { setPageTitle } = usePageTitle();
  const { delAppliance } = useApplianceMutations();
  const [firmwareUpdateOpen, setFirmwareUpdateOpen] = useState(false);
  const [firmwareUpdateAppliance, setFirmwareUpdateAppliance] =
    useState<IAppliance>();
  const { CopyToClipboardButton } = useClipboardCopy();
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editAppliance, setEditAppliance] = useState<IAppliance>();
  const [deleteAppliance, setDeleteAppliance] = useState<
    IAppliance | undefined
  >(undefined);
  const { isLoading, manufacturerLookup, modelLookup } = useApplianceLookups();

  const handleDeleteHardware = (appliance: IAppliance) => {
    setDeleteAppliance(appliance);
    setDeleteOpen(true);
  };

  const handleConfirmDeleteHardware = () => {
    delAppliance.mutate({
      ...deleteAppliance!,
    });
    setDeleteOpen(false);
  };

  const handleEditHardware = (appliance: IAppliance) => {
    setEditAppliance(appliance);
    setEditOpen(true);
  };

  const handleCreateEditClose = () => {
    editAppliance && setTimeout(() => setEditAppliance(undefined), 200);
    setEditOpen(false);
  };

  const handleFirmwareUpdate = (appliance: IAppliance) => {
    setFirmwareUpdateAppliance(appliance);
    setFirmwareUpdateOpen(true);
  };

  useEffect(() => {
    setPageTitle('Manage Appliances');
  }, [setPageTitle]);

  const cols = [
    colHelper.accessor('name', {
      header: 'Appliance Name',
      cell: (info) => (
        <div className="flex items-center space-x-4">
          <span>{info.getValue()}</span>
          {info.row.original.setupRequired && (
            <ExclamationCircleIcon className="w-5 h-5 text-amber-500" />
          )}
        </div>
      ),
    }),
    colHelper.accessor(
      (row) => manufacturerLookup(row.hardwareManufacturerId)?.name,
      { header: 'Manufacturer' }
    ),
    colHelper.accessor((row) => modelLookup(row.hardwareModelId)?.name, {
      header: 'Model',
    }),
    // colHelper.accessor((row) => Object.values(SubscriptionTier)[row.tier], {
    //   header: "Subscription",
    // }),
    colHelper.accessor('installedDate', {
      header: 'Install Date',
      cell: (info) =>
        !info.getValue() ? (
          <span className="opacity-70 dark:text-white italic">not set</span>
        ) : (
          date.format(new Date(info.getValue()), 'DD/MM/YYYY')
        ),
    }),
    colHelper.accessor('serviceDue', {
      header: 'Service Due',
      cell: (info) =>
        !info.getValue() ? (
          <span className="opacity-70 dark:text-white italic">not set</span>
        ) : (
          Math.round(
            (new Date(info.getValue()).getTime() - Date.now()) /
              (1000 * 60 * 60 * 24)
          ) + ' days'
        ),
    }),
    colHelper.display({
      header: 'Actions',
      cell: (info) => (
        <div className="flex items-center space-x-4">
          {isInstaller && (
            <>
              <CloudArrowUpIcon
                className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
                onClick={() => handleFirmwareUpdate(info.row.original)}
              />
              <CopyToClipboardButton iconOnly copyText={info.row.original.id} />
            </>
          )}
          <PencilIcon
            className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
            onClick={() => handleEditHardware(info.row.original)}
          />
          <TrashIcon
            className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
            onClick={() => handleDeleteHardware(info.row.original)}
          />
          <WrenchScrewdriverIcon
            className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
            onClick={() =>
              navigate('/faults', {
                state: { applianceId: info.row.original.id },
              })
            }
          />
        </div>
      ),
    }),
  ];

  return (
    <>
      {firmwareUpdateAppliance && (
        <ApplianceFirmwareUpdate
          open={firmwareUpdateOpen}
          onClose={() => setFirmwareUpdateOpen(false)}
          appliance={firmwareUpdateAppliance!}
        />
      )}
      <CreateEditAppliance
        open={editOpen}
        onClose={handleCreateEditClose}
        appliance={editAppliance}
      />
      <ConfirmDelete
        itemName={deleteAppliance?.name}
        open={deleteOpen}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={() => handleConfirmDeleteHardware()}
      />
      <Card title="All Appliances">
        <div className="my-6">
          <Button onClick={() => setEditOpen(true)}>Create an Appliance</Button>
        </div>
        {isLoading ? (
          <SectionLoading />
        ) : (
          <div>
            <DataTable<IAppliance>
              url="/appliance"
              queryKey={applianceQueryKeys.all()}
              columns={cols}
            />
          </div>
        )}
      </Card>
    </>
  );
}
